import {
  AnnualStatementFeeReminderTextMessagePayload
} from "@models/text-messages-templates/payload/annual-statement-fee-reminder-text-message-payload";
import {
  AnnualStatementSigningReminderTextMessagePayload
} from "@models/text-messages-templates/payload/annual-statement-signing-reminder-text-message-payload";
import {
  AsicDocumentSigningReminderTextMessagePayload
} from "@models/text-messages-templates/payload/asic-document-signing-reminder-text-message-payload";
import {
  CompanyDebtReminderTextMessagePayload
} from "@models/text-messages-templates/payload/company-debt-reminder-text-message-payload";
import { TemplateCodePreview, TemplateCodePreviewItem } from "../text-messages-templates/text-messages-helper";
import { AnnualStatementEmailPayload } from "./payloads/annual-statement-email-payload";
import { AnnualStatementEsignEmailPayload } from "./payloads/annual-statement-esign-email-payload";
import { AnnualStatementDebtReminderEmailPayload } from "./payloads/annual-statement-debt-reminder-email-payload";
import { AsicDebtReminderEmailPayload } from "./payloads/asic-debt-reminder-email-payload";
import { AsicDocumentsEmailPayload } from "./payloads/asic-documents-email-payload";
import { AsicDocumentsEsignEmailPayload } from "./payloads/asic-documents-esign-email-payload";
import { AsicDocumentsSignReminderEmailPayload } from "./payloads/asic-documents-sign-reminder-email-payload";
import { AnnualStatementSignReminderEmailPayload } from "./payloads/annual-statement-sign-reminder-email-payload";
import { AnnualStatementEsignReminderEmailPayload } from "./payloads/annual-statement-esign-reminder-email-payload";
import { AsicDocumentsEsignReminderEmailPayload } from "./payloads/asic-documents-esign-reminder-email-payload";
import { AsicDocumentsEsignCompletedEmailPayload } from "./payloads/asic-documents-esign-completed-email-payload";
import { AsicDocumentsReviewEmailPayload } from "./payloads/asic-documents-review-email-payload";
import { DirectorIdNotificationEmailPayload } from "./payloads/director-id-notification-email-payload";
import { EmailTemplate } from "./email-template";
import { EmailPreviewRequest } from "./email-preview-request";
import { EmailPayloadBase } from "./email-payload-base";
import { EmailSendRequest } from "./email-send-request";
import { UserInviteEmailPayload } from "./payloads/user-invite-email-payload";
import { AsicDocumentsBulkEsignEmailPayload } from "./payloads/asic-documents-bulk-esign-email-payload";
import { AnnualStatementBulkEsignEmailPayload } from "./payloads/annual-statement-bulk-esign-email-payload";
import { AsicInvoiceEmailPayload } from "./payloads/asic-invoice-email-payload";

export type EmailTemplateKey = 'as-email'
  | 'as-esign'
  | 'as-esign-bulk'
  | 'as-debt-reminder'
  | 'asic-debt-reminder'
  | 'asic-documents'
  | 'asic-documents-esign'
  | 'asic-documents-esign-bulk'
  | 'asic-documents-sign-reminder'
  | 'as-sign-reminder'
  | 'as-esign-reminder'
  | 'asic-invoice'
  | 'asic-documents-esign-reminder'
  | 'asic-documents-esign-completed'
  | 'asic-documents-review'
  | 'director-id-notification'
  | 'user-invite';

export class EmailTemplatesHelper {
  static TemplateHeadersRecord: Record<EmailTemplateKey, string> = {
    'as-email': 'Annual Statement (Email)',
    'as-esign': 'eSigning Annual Statement',
    'as-esign-bulk': 'Bulk eSigning Annual Statement',
    'as-debt-reminder': 'Annual Statement Debt Reminder',
    'asic-debt-reminder': 'Debt Reminder',
    'asic-invoice': 'ASIC Invoice (Email)',
    'asic-documents': 'ASIC Form(s) and Documents (Email)',
    'asic-documents-esign': 'eSigning ASIC Form(s) and Documents',
    'asic-documents-esign-bulk': 'Bulk eSigning ASIC Form(s) and Documents',
    'asic-documents-sign-reminder': 'ASIC Form(s) and Documents Signing Reminder (Email)',
    'as-sign-reminder': 'Annual Statement Signing Reminder (Email)',
    'as-esign-reminder': 'Annual Statement eSigning Reminder',
    'asic-documents-esign-reminder': 'ASIC Form(s) and Documents eSigning Reminder',
    'asic-documents-esign-completed': 'Completed eSigning Documents',
    'asic-documents-review': 'Document Review',
    'director-id-notification': 'Director ID Notification',
    'user-invite': 'User Invitation',
  };

  static emailTemplateCodesRecord: Record<string, TemplateCodePreviewItem> = {
    organisationName: { key: '{{OrganisationName}}', description: 'Account name' },
    organisationEmail: { key: '{{OrganisationEmail}}', description: 'Account email address' },
    organisationPhone: { key: '{{OrganisationPhone}}', description: 'Phone number' },
    tenDaysBeforeLodgementDeadline: { key: '{{TenDaysBeforeLodgementDeadline}}', description: 'A date that is ten days before the actual lodgement deadline' },
    tenDaysBeforeLodgementDeadlineShortFormat: { key: '{{TenDaysBeforeLodgementDeadlineShortFormat}}', description: 'A date that is ten days before the actual lodgement deadline' },
    documentName: { key: '{{DocumentName}}', description: 'Type of ASIC Form that was sent to the director(s)' },
    lodgementDeadline: { key: '{{LodgementDeadline}}', description: 'Lodgement deadline of the ASIC form' },
    partner: { key: '{{Partner}}', description: 'Name of the Partner assigned to the company' },
    asicContact: { key: '{{AsicContact}}', description: 'Name of the assigned ASIC contact in the Agent Details section' },
    accountManager: { key: '{{AccountManager}}', description: 'Name of the Account Manager' },
    billingContactFirstName: { key: '{{BillingContactFirstName}}', description: 'First name of the billing contact assigned in the company profile' },
    billingContactFullName: { key: '{{BillingContactFullName}}', description: 'Full name of the billing contact assigned in the company profile' },
    payUrl: { key: '{{PayUrl}}', description: 'The “Pay Now” button should route end users to the AusPost Billpay page' },
    companyName: { key: '{{CompanyName}}', description: 'Name of company' },
    companyACN: { key: '{{CompanyACN}}', description: 'ACN' },
    annualReviewFee: { key: '{{AnnualReviewFee}}', description: 'Annual review fee' },
    annualReviewFeeDeadline: { key: '{{AnnualReviewFeeDeadline}}', description: 'Payment deadline for the annual review fee' },
    annualReviewFeeDeadlineShortFormat: { key: '{{AnnualReviewFeeDeadlineShortFormat}}', description: 'Annual review fee due date' },
    debt: { key: '{{Debt}}', description: 'Debt in the company profile page' },
    dueDate: { key: '{{dueDate}}', description: 'Due date of the ASIC fee' },
    dueDateShortFormat: { key: '{{DueDateShortFormat}}', description: 'Due date of the ASIC fee' },
    directorFirstName: { key: '{{DirectorFirstName}}', description: 'First name of the director who needs to sign the docs' },
    directorFullName: { key: '{{DirectorFullName}}', description: 'Full name of the director who needs to sign the docs' },
    documentType: { key: '{{DocumentType}}', description: 'Type of document' },
    lodgementDeadlineShortFormat: { key: '{{LodgementDeadlineShortFormat}}', description: 'Lodgement deadline of the ASIC form' },
    year: { key: '{{Year}}', description: 'The year the annual statement was issued by ASIC' },
    lateFeeForOneMonth: { key: '{{LateFeeForOneMonth}}', description: 'Late fee for up to one month late' },
    lateFeeForMoreThanOneMonth: { key: '{{LateFeeForMoreThanOneMonth}}', description: 'Late fee for more than one month' },
    username: { key: '{{Username}}', description: 'Name of the Pravo user who sends the annual statement' },
    signUrl: { key: '{{SignUrl}}', description: 'The “Sign Now” button should route the user to the page where to sign the documents' },
    reviewerName: { key: '{{ReviewerName}}', description: 'Name of the Approver for sent documents' },
    reviewUrl: { key: '{{ReviewUrl}}', description: 'The “Review” button should route the user to the page where to review the sent documents' },
    companyNames: { key: '{{CompanyNames}}', description: 'Names of companies that person is a director for' },
    firstName: { key: '{{FirstName}}', description: 'First name of the user who receives the invitation' },
    fullName: { key: '{{FullName}}', description: 'First name of the user who receives the invitation' },
    loginUrl: { key: '{{LoginUrl}}', description: 'The “Login” button should route the user to the page where to fill he\'s data.' },
    customMessage: { key: '{{CustomMessage}}', description: 'Custom message that can be added in bulk esign email.' },
  };

  static buildEmailSendRequest(template: EmailTemplate, to = ''): EmailSendRequest {
    const payloadModel = EmailTemplatesHelper.buildEmailTemplatePayload(template.code);
    return new EmailSendRequest({ template, payloadModel, to });
  }

  static buildEmailPreviewRequest(template: EmailTemplate, payloadModel: EmailPayloadBase | null = null): EmailPreviewRequest {
    if(!payloadModel)
        payloadModel = EmailTemplatesHelper.buildEmailTemplatePayload(template.code);

    return new EmailPreviewRequest({ template, payloadModel });
  }

  static buildEmailTemplatePayload(code: EmailTemplateKey): EmailPayloadBase {
    const payload = EmailTemplateTypesToModels[EmailTemplateCodesToTypes[code]](fullMockedData);
    if (!payload) throw new Error(`No payload model for code "${ code }"`);
    return payload;
  }

  static buildPreview(payload: EmailPayloadBase): TemplateCodePreview {
    try {
      const templateCodePreview: TemplateCodePreview = [];
      Object.keys(payload).forEach((key) => {
        if (this.emailTemplateCodesRecord[key])
          templateCodePreview.push(this.emailTemplateCodesRecord[key]);
      });

      return templateCodePreview;
    } catch (e) {
      return [];
    }
  }

  static replaceNewLine(text: string | null): string {
    return text?.replaceAll('\n', '</br>') ?? '';
  }

  static prepareTemplateString(text: string | null): string {
    return (text ?? '')
      .replaceAll('\n', '')
      .replaceAll('<br />', '\n')
      .replaceAll('&amp;', '&')
      .replaceAll('</br>', '\n');
  }
}

export type IEmailAndTextTemplatePayloadData =
  AnnualStatementEmailPayload
  & AnnualStatementEsignEmailPayload
  & AnnualStatementDebtReminderEmailPayload
  & AnnualStatementBulkEsignEmailPayload
  & AsicDebtReminderEmailPayload
  & AsicDocumentsEmailPayload
  & AsicDocumentsEsignEmailPayload
  & AsicDocumentsSignReminderEmailPayload
  & AnnualStatementSignReminderEmailPayload
  & AnnualStatementEsignReminderEmailPayload
  & AsicDocumentsEsignReminderEmailPayload
  & AsicDocumentsEsignCompletedEmailPayload
  & AsicDocumentsReviewEmailPayload
  & DirectorIdNotificationEmailPayload
  & AnnualStatementFeeReminderTextMessagePayload
  & AnnualStatementSigningReminderTextMessagePayload
  & AsicDocumentSigningReminderTextMessagePayload
  & CompanyDebtReminderTextMessagePayload
  & AsicDocumentsBulkEsignEmailPayload
  & UserInviteEmailPayload;

export const fullMockedData: IEmailAndTextTemplatePayloadData = {
  $type: 'EmailTemplate',
  organisationName: 'Sample Organisation',
  organisationEmail: 'sample@organisation.com',
  organisationPhone: '+1234567890',

  companyName: 'Test Company Pty Ltd',
  companyACN: '123456789',
  directorFirstName: 'John',
  directorFullName: 'John Doe',
  year: new Date().getFullYear().toString(),
  annualReviewFee: '28$',
  //01 January 2024 format
  annualReviewFeeDeadline: '01 January 2024',
  //01/01/2024 format
  annualReviewFeeDeadlineShortFormat: '01/01/2024',
  dueDate: '01/01/2024',
  dueDateShortFormat: '01/01/2024',
  debt: '1000.00',
  lateFeeForOneMonth: '98',
  lateFeeForMoreThanOneMonth: '411',
  username: 'johndoe123',
  partner: 'Partner Name',
  asicContact: 'ASIC Contact',
  accountManager: 'Account Manager',
  billingContactFirstName: 'Jane',
  billingContactFullName: 'Jane Smith',
  payUrl: 'https://example.com/pay',
  firstName: 'Jane',
  fullName: 'Jane Smith',
  loginUrl: 'https://example.com/pay',

  documentName: 'Test Document',
  lodgementDeadline: '31 December 2024',
  lodgementDeadlineShortFormat: '31/12/2024',
  tenDaysBeforeLodgementDeadline: '21 December 2024',
  tenDaysBeforeLodgementDeadlineShortFormat: '21/12/2024',
  signUrl: 'https://example.com/sign',

  reviewerName: 'Reviewing Officer',
  reviewUrl: 'https://example.com/review',

  companyNames: ['Company A', 'Company B'],
  documentType: 'c:362',
  customMessage: 'This is a custom message for the email template.',
};

export const EmailTemplateCodesToTypes: Record<EmailTemplateKey, string> = {
  ['as-email']: 'AnnualStatementEmailPayload',
  ['as-esign']: 'AnnualStatementEsignEmailPayload',
  ['as-esign-bulk']: 'AnnualStatementBulkEsignEmailPayload',
  ['as-debt-reminder']: 'AnnualStatementDebtReminderEmailPayload',
  ['asic-debt-reminder']: 'AsicDebtReminderEmailPayload',
  ['asic-documents']: 'AsicDocumentsEmailPayload',
  ['asic-documents-esign']: 'AsicDocumentsEsignEmailPayload',
  ['asic-documents-esign-bulk']: 'AsicDocumentsBulkEsignEmailPayload',
  ['asic-documents-sign-reminder']: 'AsicDocumentsSignReminderEmailPayload',
  ['as-sign-reminder']: 'AnnualStatementSignReminderEmailPayload',
  ['as-esign-reminder']: 'AnnualStatementEsignReminderEmailPayload',
  ['asic-invoice']: 'AsicInvoiceEmailPayload',
  ['asic-documents-esign-reminder']: 'AsicDocumentsEsignReminderEmailPayload',
  ['asic-documents-esign-completed']: 'AsicDocumentsEsignCompletedEmailPayload',
  ['asic-documents-review']: 'AsicDocumentsReviewEmailPayload',
  ['director-id-notification']: 'DirectorIdNotificationEmailPayload',
  ['user-invite']: 'UserInviteEmailPayload',
};

export const EmailTemplateTypesToModels: Record<string, (data: Partial<EmailPayloadBase>) => EmailPayloadBase> = {
  [AnnualStatementEmailPayload.$type]: (data) => new AnnualStatementEmailPayload(data),
  [AnnualStatementEsignEmailPayload.$type]: (data) => new AnnualStatementEsignEmailPayload(data),
  [AnnualStatementBulkEsignEmailPayload.$type]: (data) => new AnnualStatementBulkEsignEmailPayload(data),
  [AnnualStatementDebtReminderEmailPayload.$type]: (data) => new AnnualStatementDebtReminderEmailPayload(data),
  [AsicDebtReminderEmailPayload.$type]: (data) => new AsicDebtReminderEmailPayload(data),
  [AsicInvoiceEmailPayload.$type]: (data) => new AsicInvoiceEmailPayload(data),
  [AsicDocumentsEmailPayload.$type]: (data) => new AsicDocumentsEmailPayload(data),
  [AsicDocumentsEsignEmailPayload.$type]: (data) => new AsicDocumentsEsignEmailPayload(data),
  [AsicDocumentsBulkEsignEmailPayload.$type]: (data) => new AsicDocumentsBulkEsignEmailPayload(data),
  [AsicDocumentsSignReminderEmailPayload.$type]: (data) => new AsicDocumentsSignReminderEmailPayload(data),
  [AnnualStatementSignReminderEmailPayload.$type]: (data) => new AnnualStatementSignReminderEmailPayload(data),
  [AnnualStatementEsignReminderEmailPayload.$type]: (data) => new AnnualStatementEsignReminderEmailPayload(data),
  [AsicDocumentsEsignReminderEmailPayload.$type]: (data) => new AsicDocumentsEsignReminderEmailPayload(data),
  [AsicDocumentsEsignCompletedEmailPayload.$type]: (data) => new AsicDocumentsEsignCompletedEmailPayload(data),
  [AsicDocumentsReviewEmailPayload.$type]: (data) => new AsicDocumentsReviewEmailPayload(data),
  [DirectorIdNotificationEmailPayload.$type]: (data) => new DirectorIdNotificationEmailPayload(data),
  [UserInviteEmailPayload.$type]: (data) => new UserInviteEmailPayload(data),
};
